import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusWidgetTheme } from "scmp-app/components/plus/plus-widget";
import LogoDarkPlus from "scmp-app/components/plus/plus-widget/logo-dark-plus.svg";
import LogoLightPlus from "scmp-app/components/plus/plus-widget/logo-light-plus.svg";
import { PlusWidgetDailyPulse } from "scmp-app/components/plus/plus-widget/plus-widget-daily-pulse";
import { PlusWidgetHighlights } from "scmp-app/components/plus/plus-widget/plus-widget-highlights";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { plusWidgetHasAccessQuery$key } from "scmp-app/queries/__generated__/plusWidgetHasAccessQuery.graphql";

import {
  Container,
  DailyPulse,
  HighlightsFromScmpPlus,
  LogoPlusContainer,
  NewsAgenda,
  StyledPlusWidgetNewsAgenda,
  Title,
} from "./styles";

type Props = {
  className?: string;
  pageType: "homepage" | "section" | "topic";
  plusWidgetTheme: PlusWidgetTheme;
  reference: plusWidgetHasAccessQuery$key;
};

type Settings = {
  custom_text?: string;
};

export const PlusWidgetHasAccess: FunctionComponent<Props> = ({
  className,
  pageType,
  plusWidgetTheme = PlusWidgetTheme.DARK,
  reference,
}) => {
  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: pageType,
        widget_name: "content_discovery",
        widget_version: "subscriber",
      },
      subcategory: "widget",
    }),
    [pageType],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  const data = useFragment(
    graphql`
      fragment plusWidgetHasAccessQuery on Query
      @argumentDefinitions(
        applicationId: { type: "String!" }
        dailyPulseTypeUuId: { type: "String!" }
        highlightQueueName: { type: "String!" }
        plusNewsAgendaStartDate: { type: "Timestamp!" }
        plusNewsAgendaEndDate: { type: "Timestamp!" }
        plusNewsAgendaTypes: { type: "[String]!" }
      ) {
        ...plusWidgetDailyPulseQuery @arguments(dailyPulseTypeUuId: $dailyPulseTypeUuId)
        ...plusWidgetHighlightsQuery @arguments(queueName: $highlightQueueName)
        ...plusWidgetNewsAgendaQuery
          @arguments(
            applicationId: $applicationId
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
        highlightQueue: queue(filter: { name: $highlightQueueName }) {
          settings
        }
      }
    `,
    reference,
  );
  const customText = (data.highlightQueue?.settings as Settings)?.custom_text;
  const displayText = customText ?? "";

  return (
    <Container
      $plusWidgetTheme={plusWidgetTheme}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      <LogoPlusContainer>
        {plusWidgetTheme === PlusWidgetTheme.LIGHT ? <LogoLightPlus /> : <LogoDarkPlus />}
      </LogoPlusContainer>
      <DailyPulse>
        <Title>DAILY PULSE</Title>
        <PlusWidgetDailyPulse
          pageType={pageType}
          plusWidgetTheme={plusWidgetTheme}
          reference={data}
        />
      </DailyPulse>
      <NewsAgenda>
        <Title>NEWS AGENDA</Title>
        <StyledPlusWidgetNewsAgenda
          pageType={pageType}
          plusWidgetTheme={plusWidgetTheme}
          reference={data}
        />
      </NewsAgenda>
      <HighlightsFromScmpPlus>
        <Title>{displayText}</Title>
        <PlusWidgetHighlights
          pageType={pageType}
          plusWidgetTheme={plusWidgetTheme}
          reference={data}
        />
      </HighlightsFromScmpPlus>
    </Container>
  );
};

PlusWidgetHasAccess.displayName = "PlusWidgetHasAccess";
